import clsx from 'clsx';
import { memo } from 'react';
import { useDropzone } from 'react-dropzone';
import { defineMessages, useIntl } from 'react-intl';

import type { FileInputProps } from './types';

import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import './FileInput.css';
import { resolveFileTypeList, toFileInputRejections } from './utils';

const messages = defineMessages({
  activeDragAndDrop: {
    id: 'FileInput.instructionsText.activeDragAndDrop',
    defaultMessage: 'Drop files here…',
    description: 'Instructions shown while files are dragged over the area',
  },
  dragAndDrop: {
    id: 'FileInput.defaultPlaceholderText.dragAndDrop',
    defaultMessage: 'Drag and drop',
    description:
      'Default bolded placeholder instructions for drag and drop area',
  },
  multiFileType: {
    id: 'FileInput.defaultPlaceholderText.multiFileType',
    defaultMessage: '{fileTypeList} files',
    description:
      "File type list for the multi-file placeholder. Example: fileTypeList = '.docx or .pdf'",
  },
  separator: {
    id: 'FileInput.conjunctionText',
    defaultMessage: 'or',
    description:
      'Separator between dragging files in and choosing them from the computer',
  },
  singleFileType: {
    id: 'FileInput.defaultPlaceholderText.singleFileType',
    defaultMessage: 'a {fileTypeList} file',
    description:
      "File type list for the single-file placeholder. Example: fileTypeList = '.pdf'",
  },
  uploadButton: {
    id: 'FileInput.uploadButton',
    defaultMessage: 'Upload from computer',
    description: 'Button that opens the file picker',
  },
});

export const FileInput = memo(function FileInput({
  label,
  name,
  onChange,
  accept = {},
  disabled,
  isMulti = false,
  maxFiles,
  maxSize,
  minSize,
  placeholder,
  required,
  status,
  onReject,
  __private,
}: FileInputProps) {
  const intl = useIntl();
  const { ariaDescribedBy, ariaLabelledBy, id } = __private ?? {};

  // `noClick` / `noKeyboard` keep activation on the button below: the area also
  // holds it, and a clickable, focusable area around a button is a nested
  // interactive target.
  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept,
    disabled,
    maxFiles,
    maxSize,
    minSize,
    multiple: isMulti,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted, rejected) => {
      onChange(accepted.filter((file) => file.size > 0));

      if (!onReject) {
        return;
      }

      const rejections = [
        ...toFileInputRejections(rejected),
        ...accepted
          .filter((file) => file.size === 0)
          .map((file) => ({ file, reasons: ['empty' as const] })),
      ];

      if (rejections.length > 0) {
        onReject(rejections);
      }
    },
  });

  const fileTypeList = resolveFileTypeList(accept, intl.locale);
  const uploadLabel = intl.formatMessage(messages.uploadButton);

  let instructions;
  if (isDragActive) {
    instructions = (
      <Text as="strong" variant="body-semi-m">
        {intl.formatMessage(messages.activeDragAndDrop)}
      </Text>
    );
  } else if (placeholder) {
    instructions = (
      <Text as="span" variant="body-m">
        {placeholder}
      </Text>
    );
  } else {
    instructions = (
      <>
        <Text as="strong" variant="body-semi-m">
          {intl.formatMessage(messages.dragAndDrop)}
        </Text>
        {fileTypeList ? (
          <span className="eds-file-input--hint">
            <Text as="span" tone="subdued" variant="body-m">
              {intl.formatMessage(
                isMulti ? messages.multiFileType : messages.singleFileType,
                { fileTypeList },
              )}
            </Text>
          </span>
        ) : null}
      </>
    );
  }

  return (
    <div className="eds-file-input--host">
      <div
        {...getRootProps({
          'aria-describedby': ariaDescribedBy,
          'aria-label': ariaLabelledBy ? undefined : label,
          'aria-labelledby': ariaLabelledBy,
          className: clsx(
            'eds-file-input',
            disabled && 'eds-file-input--disabled',
            isDragActive && 'eds-file-input--drag-active',
            status && `eds-file-input--${status}`,
          ),
          id,
          role: 'group',
        })}
      >
        {/* Icon and instructions travel as one flex item: flex breaks lines on
            an item's hypothetical width, so a long instruction left on its own
            would push the icon onto a line by itself. */}
        <span className="eds-file-input--prompt">
          <Icon icon="upload" size="m" />
          <p className="eds-file-input--instructions">{instructions}</p>
        </span>
        <span className="eds-file-input--separator">
          <Text as="span" tone="subdued" variant="body-semi-s">
            {intl.formatMessage(messages.separator)}
          </Text>
        </span>
        <Button
          disabled={disabled}
          label={uploadLabel}
          size="s"
          text={uploadLabel}
          onClick={open}
        />
        <input {...getInputProps({ name, required })} />
      </div>
    </div>
  );
});

FileInput.displayName = 'FileInput';
